import { useEffect, useRef, useState } from "react";
import { syncApiUrl, setAuthToken, getAuthToken, SYNC_REJECTED_EVENT } from "@cliqsuite/db/powersync";
import type { PairedDevice } from "../types";
import { readPairedDevice, writePairedDevice } from "../lib/local-store";
import { useLive } from "../lib/db";
import { ensurePosSyncStarted, stopPosSync } from "../components/PosDbProvider";

// A "not paired" reading must hold this long (re-checked against fresh rows) before the till
// forgets its pairing, so a momentary blip can never unpair a till that's still valid.
const UNPAIR_CONFIRM_MS = 180_000;

/** Forget this till's pairing and its synced data. Queued offline sales are kept (local-only). */
async function forgetPairing(): Promise<void> {
  await stopPosSync().catch(() => undefined);
  setAuthToken(null);
  writePairedDevice(null);
}

/** Staff PIN gate phase 1: bind this physical till to a business/store via a pairing code. */
export function useDevicePairing() {
  const [device, setDevice] = useState<PairedDevice | null>(() => readPairedDevice());
  const [error, setError] = useState("");
  const [pairing, setPairing] = useState(false);
  /** Set when Owner Portal unpaired this till while it was running: shows the "unpaired" screen. */
  const [remoteUnpaired, setRemoteUnpaired] = useState<{ storeName: string; registerName: string } | null>(null);

  // Only this till's own store's devices ever sync here (store bucket). Used to notice a
  // remote unpair from Owner Portal.
  const { data: devices, isReady: devicesReady } = useLive("devices", "SELECT * FROM devices WHERE id = $1", [device?.deviceId ?? ""]);
  const devicesRef = useRef(devices);
  devicesRef.current = devices;

  // This till's own local `device` state was only ever read from
  // localStorage once at startup, and otherwise only ever cleared by its
  // own "Unpair this device" button — so an unpair (or delete, or a "new
  // pairing code" regeneration) done remotely from Owner Portal synced
  // straight past it: Owner Portal correctly showed "Pending", but the
  // till kept right on acting paired, since nothing here ever re-checked
  // its own device row. Gated on devicesReady so this can't fire against a
  // half-synced collection on a fresh load and wrongly unpair a device
  // that's actually still valid — same isReady-vs-data pitfall already hit
  // (and fixed) for useStaffSession's saved-session rehydration.
  //
  // Unlike that fix, this can't just check once at mount — it needs to
  // keep catching a genuine remote unpair for the rest of the session. So
  // instead a "not paired" reading only takes effect after UNPAIR_CONFIRM_MS,
  // re-verified then against a fresh snapshot (devicesRef, not the `devices`
  // this render closed over) rather than acting immediately — a same-render
  // blip that self-corrects on the next sync cancels the pending timer via
  // this effect's own cleanup and never unpairs anything.
  useEffect(() => {
    if (!device || !devicesReady) return;
    const match = (devices ?? []).find((d) => d.id === device.deviceId);
    if (match && match.is_paired) return;
    const timer = setTimeout(() => {
      const latest = devicesRef.current ?? [];
      const stillMatch = latest.find((d) => d.id === device.deviceId);
      if (stillMatch && stillMatch.is_paired) return;
      setRemoteUnpaired({ storeName: device.storeName, registerName: device.registerName });
      setDevice(null);
      void forgetPairing();
    }, UNPAIR_CONFIRM_MS);
    return () => clearTimeout(timer);
  }, [device, devices, devicesReady]);

  // Fail-safe for a till whose pairing the server no longer accepts but which can't learn that
  // from sync (its token is refused, so the devices table never updates), or which is paired
  // locally with no token at all. Without this it would keep selling offline forever with
  // nothing reaching the server. Queued sales are kept and sent after pairing again.
  useEffect(() => {
    if (!device) return;
    const needsPairing = () => {
      setRemoteUnpaired({ storeName: device.storeName, registerName: device.registerName });
      setDevice(null);
      void forgetPairing();
    };
    if (!getAuthToken()) {
      needsPairing();
      return;
    }
    window.addEventListener(SYNC_REJECTED_EVENT, needsPairing);
    return () => window.removeEventListener(SYNC_REJECTED_EVENT, needsPairing);
  }, [device]);

  // Picks up server-side changes to THIS till's own device row (currently
  // just bypass_geofence — see admin-portal's Devices page) once per load.
  // `device`'s only other source is pair()'s own response, captured once at
  // pairing time and otherwise never touched again — an already-paired
  // till reloading (or just sitting open) had no way to notice a flag
  // flipped from admin-portal afterward. Confirmed live: toggling
  // bypass_geofence had zero effect on an already-paired till without this.
  // POST /core/devices/refresh (not the generic GET /:id) since this till's
  // credential is a device-scope token, which the generic crudRoutes auth
  // middleware doesn't recognize at all (JWT-only) — see that route's own
  // doc for why it exists as a separate, bespoke endpoint.
  useEffect(() => {
    if (!device) return;
    const token = getAuthToken();
    if (!token) return;
    let cancelled = false;
    void fetch(syncApiUrl("/core/devices/refresh"), {
      method: "POST",
      headers: { authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body: { registerName: string; storeName: string; bypassGeofence: boolean } | null) => {
        if (cancelled || !body) return;
        setDevice((prev) => {
          if (!prev) return prev;
          const next: PairedDevice = { ...prev, registerName: body.registerName, storeName: body.storeName, bypassGeofence: body.bypassGeofence };
          writePairedDevice(next);
          return next;
        });
      })
      .catch(() => {
        // Best effort — stale local values just persist until the next successful refresh.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device?.deviceId]);

  async function pair(codeRaw: string) {
    const code = codeRaw.trim().toUpperCase();
    if (!code) {
      setError("Enter a pairing code.");
      return;
    }
    setPairing(true);
    setError("");
    try {
      // Checked server-side, which also mints this till's device-scope token: the credential
      // /powersync/token trades for a sync token limited to this store.
      const res = await fetch(syncApiUrl("/core/devices/pair"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ code, deviceType: "pos" }),
      });
      const body = (await res.json().catch(() => null)) as
        | {
            ok: true;
            deviceId: string;
            businessId: string;
            storeId: string;
            storeName: string;
            registerName: string;
            bypassGeofence: boolean;
            scopeToken: string;
          }
        | { error: string }
        | null;
      if (!res.ok || !body || "error" in body) {
        setError(
          body && "error" in body && body.error === "invalid_code"
            ? "We couldn't find that code. Check it in Owner Portal and try again."
            : "Couldn't pair. Check the connection and try again.",
        );
        return;
      }
      setAuthToken(body.scopeToken);
      // storeName/registerName come straight from the pairing response now
      // (not a lookup against stores/devicesCollection) — this till has no
      // scope token, and therefore no scoped shape read, until the line
      // above just ran, so there was never anything real to look up from
      // those collections at this exact moment anyway.
      const paired: PairedDevice = {
        deviceId: body.deviceId,
        businessId: body.businessId,
        storeId: body.storeId,
        storeName: body.storeName,
        registerName: body.registerName,
        bypassGeofence: body.bypassGeofence,
      };
      writePairedDevice(paired);
      setDevice(paired);
      ensurePosSyncStarted();
    } finally {
      setPairing(false);
    }
  }

  async function unpair() {
    try {
      const token = getAuthToken();
      if (device && token) {
        // Online, best effort: the local copy is about to be cleared, so it can't be queued.
        await fetch(syncApiUrl(`/core/devices/${device.deviceId}`), {
          method: "PATCH",
          headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
          body: JSON.stringify({ is_paired: false, device_name: null }),
        });
      }
    } finally {
      await forgetPairing();
      window.location.reload();
    }
  }

  return { device, remoteUnpaired, error, pairing, pair, unpair, clearError: () => setError("") };
}
